/**
 * Adapter functions to convert existing data structures to the unified GroundingSearchItem type
 */

import { parseSiteScope } from "@repo/core/client";
import type { CompetitorRanking, GroundingSearchItem } from "./types";

/**
 * Convert AIVisibilityRanking from properties API to GroundingSearchItem
 */
export interface AIVisibilityRankingInput {
	groundingSearchId: string;
	searchQuery: string;
	usageCount: number;
	discoveryQueries: Array<{
		id: string;
		query: string;
	}>;
	platforms?: string[];
	googleUnavailable?: boolean;
	braveUnavailable?: boolean;
	/** True when Brave search was skipped due to rate limiting error. */
	braveError?: boolean;
	brandRankGoogle: number | null;
	brandUrlGoogle: string | null;
	brandRankBrave: number | null;
	brandUrlBrave: string | null;
	isGapGoogle: boolean;
	isGapBrave: boolean;
	isGapBoth: boolean;
	competitorRankings: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rankGoogle: number | null;
		urlGoogle: string | null;
		rankBrave: number | null;
		urlBrave: string | null;
	}>;
	impactScore: number;
	/** Set by `getAiVisibilityRankings` when the caller opted into scoped rows. */
	siteScopeDomain?: string | null;
	siteScopeClass?: "own" | "competitor" | "third_party" | null;
	siteScopeCompetitorName?: string | null;
}

export function adaptAIVisibilityRanking(
	ranking: AIVisibilityRankingInput,
): GroundingSearchItem {
	return {
		id: ranking.groundingSearchId,
		searchQuery: ranking.searchQuery,
		usageCount: ranking.usageCount,
		discoveryQueries: ranking.discoveryQueries.map((dq) => ({
			id: dq.id,
			query: dq.query,
		})),
		platforms: ranking.platforms,
		googleUnavailable: ranking.googleUnavailable,
		braveUnavailable: ranking.braveUnavailable,
		braveError: ranking.braveError,
		brandRankGoogle: ranking.brandRankGoogle,
		brandUrlGoogle: ranking.brandUrlGoogle,
		brandRankBrave: ranking.brandRankBrave,
		brandUrlBrave: ranking.brandUrlBrave,
		competitorRankings: ranking.competitorRankings.map((comp) => ({
			competitorId: comp.competitorId,
			competitorName: comp.competitorName,
			competitorDomain: comp.competitorDomain,
			rankGoogle: comp.rankGoogle,
			urlGoogle: comp.urlGoogle,
			rankBrave: comp.rankBrave,
			urlBrave: comp.urlBrave,
		})),
		isGapGoogle: ranking.isGapGoogle,
		isGapBrave: ranking.isGapBrave,
		isGapBoth: ranking.isGapBoth,
		impactScore: ranking.impactScore,
		siteScopeDomain: ranking.siteScopeDomain ?? null,
		siteScopeClass: ranking.siteScopeClass ?? null,
		siteScopeCompetitorName: ranking.siteScopeCompetitorName ?? null,
	};
}

export function adaptAIVisibilityRankings(
	rankings: AIVisibilityRankingInput[],
): GroundingSearchItem[] {
	return rankings.map(adaptAIVisibilityRanking);
}

/**
 * Convert GroundingSearchOpportunity from recommendations to GroundingSearchItem
 */
export interface GroundingSearchOpportunityInput {
	groundingSearchId: string;
	searchQuery: string;
	usageCount: number;
	discoveryQueries: Array<{
		queryId: string;
		query: string;
		queryType?: string;
	}>;
	brandRankGoogle?: number | null;
	brandUrlGoogle?: string | null;
	brandRankBrave?: number | null;
	brandUrlBrave?: string | null;
	/** @deprecated Old reports persisted *Bing keys (always null); read for back-compat. */
	brandRankBing?: number | null;
	brandUrlBing?: string | null;
	// Some versions have separate arrays per engine
	competitorRankingsGoogle?: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rank: number;
		url: string;
	}>;
	competitorRankingsBrave?: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rank: number;
		url: string;
	}>;
	/** @deprecated Old reports persisted competitorRankingsBing (always empty). */
	competitorRankingsBing?: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rank: number;
		url: string;
	}>;
	// Unified structure
	competitorRankings?: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rank?: number;
		rankGoogle?: number | null;
		urlGoogle?: string | null;
		rankBrave?: number | null;
		urlBrave?: string | null;
		rankBing?: number | null;
		urlBing?: string | null;
	}>;
	isGapGoogle?: boolean;
	isGapBrave?: boolean;
	isGapBing?: boolean;
	isGapBoth?: boolean;
	isGap?: boolean;
	gapScore: number;
	/** Platforms that produced this search (e.g. openai, gemini, claude). Report recommendations use platformsUsed. */
	platforms?: string[];
	platformsUsed?: string[];
}

function isBraveRunForPlatform(platform: string): boolean {
	return (platform || "").toLowerCase() === "claude";
}

function isGoogleRunForPlatform(platform: string): boolean {
	return (platform || "").toLowerCase() !== "claude";
}

export function adaptGroundingSearchOpportunity(
	opp: GroundingSearchOpportunityInput,
): GroundingSearchItem {
	// Merge per-engine competitor rankings into the unified shape. Brave arrays are
	// the renamed Bing arrays; old reports only ever had (null) Bing data.
	const competitorRankings: CompetitorRanking[] = [];
	const competitorRankingsBrave =
		opp.competitorRankingsBrave ?? opp.competitorRankingsBing;

	if (opp.competitorRankingsGoogle || competitorRankingsBrave) {
		const competitorMap = new Map<string, CompetitorRanking>();

		(opp.competitorRankingsGoogle || []).forEach((comp) => {
			competitorMap.set(comp.competitorId, {
				competitorId: comp.competitorId,
				competitorName: comp.competitorName,
				competitorDomain: comp.competitorDomain,
				rankGoogle: comp.rank,
				urlGoogle: comp.url,
				rankBrave: null,
				urlBrave: null,
			});
		});

		(competitorRankingsBrave || []).forEach((comp) => {
			const existing = competitorMap.get(comp.competitorId);
			if (existing) {
				existing.rankBrave = comp.rank;
				existing.urlBrave = comp.url;
			} else {
				competitorMap.set(comp.competitorId, {
					competitorId: comp.competitorId,
					competitorName: comp.competitorName,
					competitorDomain: comp.competitorDomain,
					rankGoogle: null,
					urlGoogle: null,
					rankBrave: comp.rank,
					urlBrave: comp.url,
				});
			}
		});

		competitorRankings.push(...Array.from(competitorMap.values()));
	} else if (opp.competitorRankings) {
		opp.competitorRankings.forEach((comp) => {
			competitorRankings.push({
				competitorId: comp.competitorId,
				competitorName: comp.competitorName,
				competitorDomain: comp.competitorDomain,
				rankGoogle: comp.rankGoogle ?? comp.rank ?? null,
				urlGoogle: comp.urlGoogle ?? null,
				rankBrave: comp.rankBrave ?? comp.rankBing ?? null,
				urlBrave: comp.urlBrave ?? comp.urlBing ?? null,
			});
		});
	}

	const platforms = opp.platforms ?? opp.platformsUsed ?? [];
	const googleUnavailable =
		platforms.length > 0 &&
		!platforms.some((p) => isGoogleRunForPlatform(p));
	const braveUnavailable =
		platforms.length > 0 &&
		!platforms.some((p) => isBraveRunForPlatform(p));

	const brandRankBrave = opp.brandRankBrave ?? opp.brandRankBing ?? null;
	const brandUrlBrave = opp.brandUrlBrave ?? opp.brandUrlBing ?? null;

	// Mark as a gap whenever the brand doesn't rank, but only for an engine that
	// ran (a Claude-only search is never a Google gap, and vice-versa).
	const isGapGoogle =
		opp.isGapGoogle ??
		(!googleUnavailable && (opp.isGap ?? opp.brandRankGoogle === null));
	const isGapBrave =
		opp.isGapBrave ??
		opp.isGapBing ??
		(!braveUnavailable && brandRankBrave === null);
	const isGapBoth = opp.isGapBoth ?? (isGapGoogle && isGapBrave);

	return {
		id: opp.groundingSearchId,
		searchQuery: opp.searchQuery,
		usageCount: opp.usageCount,
		discoveryQueries: opp.discoveryQueries.map((dq) => ({
			id: dq.queryId,
			query: dq.query,
			queryType: dq.queryType,
		})),
		platforms: platforms.length > 0 ? platforms : undefined,
		googleUnavailable,
		braveUnavailable,
		brandRankGoogle: opp.brandRankGoogle ?? null,
		brandUrlGoogle: opp.brandUrlGoogle ?? null,
		brandRankBrave,
		brandUrlBrave,
		competitorRankings,
		isGapGoogle,
		isGapBrave,
		isGapBoth,
		impactScore: opp.gapScore,
		// Parsed from the TEXT, not read off a column: this adapter's input is
		// persisted report JSON, which predates `GroundingSearch.siteScopeDomain`.
		// Reading a column here would leave every historical report's scoped rows
		// unlabeled and un-hideable. The class needs the property's competitor
		// list, which this pure adapter has no access to — the report surface
		// labels by domain alone.
		siteScopeDomain: parseSiteScope(opp.searchQuery).domain,
	};
}

export function adaptGroundingSearchOpportunities(
	opportunities: GroundingSearchOpportunityInput[],
): GroundingSearchItem[] {
	return opportunities.map(adaptGroundingSearchOpportunity);
}

/**
 * Convert simple grounding search data from discovery queries API
 */
export interface SimpleGroundingSearchInput {
	id: string;
	searchQuery: string;
	brandRank: number | null;
	brandUrl: string | null;
	competitorRankings: Array<{
		competitorId: string;
		competitorName: string;
		competitorDomain: string;
		rank: number;
	}>;
}

export function adaptSimpleGroundingSearch(
	search: SimpleGroundingSearchInput,
	discoveryQueryId: string,
	discoveryQueryText: string,
): GroundingSearchItem {
	// Mark as opportunity whenever brand doesn't rank (regardless of competitors)
	const isGap = search.brandRank === null;

	return {
		id: search.id,
		searchQuery: search.searchQuery,
		usageCount: 1,
		discoveryQueries: [
			{
				id: discoveryQueryId,
				query: discoveryQueryText,
			},
		],
		brandRankGoogle: search.brandRank,
		brandUrlGoogle: search.brandUrl,
		brandRankBrave: null,
		brandUrlBrave: null,
		braveUnavailable: true,
		competitorRankings: search.competitorRankings.map((comp) => ({
			competitorId: comp.competitorId,
			competitorName: comp.competitorName,
			competitorDomain: comp.competitorDomain,
			rankGoogle: comp.rank,
			urlGoogle: null,
			rankBrave: null,
			urlBrave: null,
		})),
		isGapGoogle: isGap,
		isGapBrave: false,
		isGapBoth: false,
		impactScore: isGap ? 50 : 0, // Simple impact score for single query
	};
}

export function adaptSimpleGroundingSearches(
	searches: SimpleGroundingSearchInput[],
	discoveryQueryId: string,
	discoveryQueryText: string,
): GroundingSearchItem[] {
	return searches.map((s) =>
		adaptSimpleGroundingSearch(s, discoveryQueryId, discoveryQueryText),
	);
}
